import api from './api';
import dayjs from 'dayjs';
import { applySyncStateToBreedingFields, parseSyncProtocolData } from '../utils/syncProtocolUtils';

function mergeDateAndTime(dateValue, timeValue) {
  if (!dateValue || !dayjs(dateValue).isValid()) return null;
  let merged = dayjs(dateValue).startOf('day');
  if (timeValue) {
    const time = dayjs(timeValue, ['HH:mm', 'HH:mm:ss'], true).isValid()
      ? dayjs(timeValue, ['HH:mm', 'HH:mm:ss'])
      : (dayjs(timeValue).isValid() ? dayjs(timeValue) : null);
    if (time) {
      merged = merged.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    }
  }
  return merged;
}

export function buildSyncProgramPayload(values, { isBulkMode, bulkAnimalIds, programId } = {}) {
  const schedulingSyncData = values.sync_protocol_data;
  const parsed = parseSyncProtocolData(schedulingSyncData);
  const syncGroupId = parsed.syncGroupId
    || parsed.sync_group_id
    || (!programId ? `sync_grp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` : null);
  if (syncGroupId) {
    parsed.syncGroupId = syncGroupId;
    parsed.sync_group_id = syncGroupId;
  }
  const derived = applySyncStateToBreedingFields(parsed) || {};
  const aiStep = parsed.steps?.find((step) => step.action === 'insemination');
  const first = parsed.steps?.[0];

  let start = first?.scheduledDate || parsed.day0Date || derived.sync_protocol_start_date;
  let time = first?.scheduledTime;
  if (aiStep?.actualDate) {
    start = aiStep.actualDate;
    time = aiStep.actualTime;
  }

  const merged = mergeDateAndTime(start, time);

  const payload = {
    breeding_type: 'synchronized',
    breeding_date: merged
      ? merged.format('YYYY-MM-DD HH:mm:ss')
      : dayjs().format('YYYY-MM-DD HH:mm:ss'),
    notes: values.notes || null,
    sync_protocol_name: derived.sync_protocol_name ?? null,
    sync_protocol_start_date: derived.sync_protocol_start_date ?? null,
    sync_protocol_end_date: derived.sync_protocol_end_date ?? null,
    insemination_schedule_date: derived.insemination_schedule_date ?? null,
    sync_protocol_data: derived.sync_protocol_data ?? parsed ?? null,
  };

  // A new program starts clean. Editing one only changes its schedule: the pregnancy result,
  // birth and offspring counts recorded on it since are left as they are.
  if (!programId) {
    Object.assign(payload, {
      status: 'bred',
      expected_due_date: null,
      actual_delivery_date: null,
      next_pregnancy_check_date: null,
      re_serve_check_date: null,
      dry_down_date: null,
      calving_window_start_date: null,
      calving_window_end_date: null,
      gestation_days: null,
      dam_id: null,
      dam_info: null,
      is_repeated_breeding: false,
      offspring_count: null,
      live_offspring_count: null,
      dead_offspring_count: null,
      male_offspring_count: null,
      female_offspring_count: null,
    });
  }

  if (!programId) {
    payload.sire_id = values.sire_id || null;
    payload.sire_info = values.sire_info || null;
    payload.semen_straw_id = values.semen_straw_id || null;
    payload.semen_straw_type = values.semen_straw_type || null;
    payload.semen_bull_id = values.semen_bull_id || null;
  } else if (values.semen_bull_id) {
    payload.semen_bull_id = values.semen_bull_id;
    payload.sire_id = values.sire_id || null;
    payload.sire_info = values.sire_info || null;
    payload.semen_straw_id = values.semen_straw_id || null;
    payload.semen_straw_type = values.semen_straw_type || null;
  }

  if (isBulkMode && !programId && bulkAnimalIds?.length > 0) {
    payload.animal_ids = bulkAnimalIds;
  }

  return payload;
}

export const fetchSyncProgramsOverview = async (farmId) => {
  const res = await api.get('/sync-programs/overview', { params: { farm_id: farmId } });
  return res.data;
};

export const fetchAnimalSyncPrograms = async (animalId) => {
  // Ask for sync programs only, and all of them, so older programs aren't cut off by paging
  const res = await api.get(`/animals/${animalId}/breedings`, {
    params: { breeding_type: 'synchronized', per_page: 1000 },
  });
  const rows = res.data?.data || res.data || [];
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((row) => row.breeding_type === 'synchronized');
};

export const createSyncProgram = async (animalId, payload) => {
  return api.post(`/animals/${animalId}/breedings`, payload);
};

// One request for the whole batch: the server enrols every animal or none, so a failure
// part-way can't leave some enrolled and a retry can't enrol them twice.
export const createSyncProgramsForAnimals = async (animalIds, payload) => {
  const { animal_ids, ...body } = payload;
  const ids = (animal_ids?.length ? animal_ids : animalIds).filter(Boolean);
  if (!ids.length) return null;
  return api.post(`/animals/${ids[0]}/breedings`, { ...body, animal_ids: ids });
};

export const updateSyncProgram = async (animalId, programId, payload) => {
  return api.put(`/animals/${animalId}/breedings/${programId}`, payload);
};

/** Stop a program part-way: the steps not yet given are cancelled. */
export const endSyncProgram = async (programId, reason) => {
  const res = await api.post(`/sync-programs/${programId}/end`, { reason: reason || null });
  return res.data;
};

export const deleteSyncProgram = async (animalId, programId) => {
  return api.delete(`/animals/${animalId}/breedings/${programId}`);
};

export const batchCompleteSyncSteps = async (farmId, items) => {
  const res = await api.post('/sync-programs/batch-complete-steps', {
    farm_id: farmId,
    items,
  });
  return res.data;
};

export const fetchFarmFemaleAnimals = async (farmId) => {
  const res = await api.get('/animals', {
    params: { farm_id: farmId, gender: 'female', paginate: false },
  });
  return res.data?.data || res.data || [];
};
